import logo from '../assets/images/logo.svg';
import ProfileIcon from '../assets/icons/profile.svg';
import AdviceIcon from '../assets/icons/advice.svg';
import TestSummaryIcon from '../assets/icons/test_summary.svg';
import CartIcon from '../assets/icons/cart.svg';
import UserMenu from './UserMenu';
import logOutIcon from '../assets/icons/log-out.png';
import * as Avatar from '@radix-ui/react-avatar';
import { NavLink } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { logout } from '../services/auth';
import { setViewAsRole } from '../store/userSlice';
import { toast } from 'react-toastify';
import { fetchPatients, linkPatient, syncAirtable } from '../services/patient';
import { setPatients, setLoading, setError } from '../store/patientSlice';
import useAirtableSyncStatus from '../hooks/useAirtableSyncStatus';
const PortaleHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profileHovered, setProfileHovered] = useState(false);
  const [adviceHovered, setAdviceHovered] = useState(false);
  const [testHovered, setTestHovered] = useState(false);
  const [prescriptionHovered, setPrescriptionHovered] = useState(false);
  const [orderHistoryHovered, setOrderHistoryHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const fullNameFromStore = useSelector((s) => s.user.fullName);
  const emailFromStore = useSelector((s) => s.user.email);
  const userRole = useSelector((s) => s.user.role);
  const viewAsRole = useSelector((s) => s.user.viewAsRole);
  const userOrganization = useSelector((s) => s.user.organization);
  const { patients = [], loading, error } = useSelector((state) => state.patient);
  const storageFallback = localStorage.getItem('userFullName') || '';
  const fullName = fullNameFromStore || storageFallback;
  const isSupervisor = userRole === 'Supervisor';
  const effectiveRole = isSupervisor
    ? (viewAsRole === 'Doctor/Nurse' ? 'Doctor/Nurse' : 'Patient')
    : userRole;
  const isPatient = effectiveRole === 'Patient';
  const isDoctorNurse = effectiveRole === 'Doctor/Nurse';
  const showPatientSelector = isSupervisor && effectiveRole === 'Patient';
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [linkingEmail, setLinkingEmail] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [shouldPollSyncStatus, setShouldPollSyncStatus] = useState(false);
  const [syncTaskId, setSyncTaskId] = useState(null);
  const [hasShownSyncCompleteToast, setHasShownSyncCompleteToast] = useState(false);
  const airtableSync = useAirtableSyncStatus({
    enabled: Boolean(shouldPollSyncStatus && showPatientSelector),
    pollIntervalMs: 1500,
    taskId: syncTaskId,
  });
  const isSyncComplete =
    airtableSync.status === 'completed' ||
    airtableSync.statusPayload?.result != null ||
    (typeof airtableSync.progress === 'number' && airtableSync.progress >= 100);

  useEffect(() => {
    if (isSyncComplete && shouldPollSyncStatus && !hasShownSyncCompleteToast) {
      toast.success('Completed 100%', {
        hideProgressBar: true
      });

      setIsSyncing(false);

      setTimeout(() => {
        window.location.reload();
      }, 400);

      setHasShownSyncCompleteToast(true);
      setShouldPollSyncStatus(false);
      setIsSyncing(false);
    }
  }, [isSyncComplete, shouldPollSyncStatus, hasShownSyncCompleteToast]);

  const logoSrc =
    isSupervisor && isPatient
      ? logo
      : userOrganization === 'Qualevita'
        ? 'https://member.biowell.nl/QV_logo_H.webp'
        : userOrganization === 'Life Code Solutions'
          ? 'https://member.biowell.nl/LCS_logo.png'
          : logo;

  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Load patients for supervisor
  useEffect(() => {
    if (isSupervisor) {
      const loadPatients = async () => {
        try {
          dispatch(setLoading(true));
          const data = await fetchPatients();
          dispatch(setPatients(data));
        } catch (err) {
          dispatch(setError(err.message || 'Failed to load patients'));
        }
      };
      loadPatients();
    }
  }, [isSupervisor, dispatch]);

  const handlePatientSearch = (query) => {
    setEmailSearchQuery(query);
  };

  const getFilteredPatients = () => {
    const terms = (emailSearchQuery || '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (terms.length === 0) return patients;
    return patients.filter((patient) => {
      const haystack = [
        patient.label,
        patient.fullName,
        patient.customerId,
        patient.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  };

  const filteredPatients = getFilteredPatients();

  const patientDisplayLabel = (patient) => {
    const myEmail = (emailFromStore || localStorage.getItem('userEmail') || '').toLowerCase();
    if (patient.role === 'Supervisor' && patient.email === myEmail) {
      return 'terug naar mijzelf';
    }
    return patient.label;
  };

  const handleEmailClick = async (patientEmail) => {
    if (linkingEmail || isDisconnecting) return;

    try {
      setLinkingEmail(patientEmail);
      const response = await linkPatient(patientEmail);

      if (response?.message || response?.patient_email) {
        toast.success(response.message || 'Successfully linked to patient');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to link patient';
      toast.error(errorMessage);
      setLinkingEmail(null);
    }
  };

  const handleDisconnect = async () => {
    if (linkingEmail || isDisconnecting || !emailFromStore) return;

    try {
      setIsDisconnecting(true);
      const response = await linkPatient(emailFromStore);

      if (response?.message || response?.patient_email) {
        toast.success(response.message || 'Successfully disconnected');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to disconnect';
      toast.error(errorMessage);
      setIsDisconnecting(false);
    }
  };

  const handleSyncAirtable = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isSyncing || linkingEmail || isDisconnecting) return;

    try {
      setIsSyncing(true);
      setShouldPollSyncStatus(true);
      setSyncTaskId(null);
      setHasShownSyncCompleteToast(false);
      const response = await syncAirtable();

      // Success response
      if (response) {
        const taskId =
          response.job_id ||
          response.customer_job_id ||
          response.blood_job_id ||
          response.customer_task_id ||
          response.blood_task_id ||
          response.task_id;
        if (taskId) {
          setSyncTaskId(taskId);
        } else {
          toast.info('Sync started, but no job id returned for status tracking.');
        }
      }
      setIsSyncing(false);
      airtableSync.refresh();
    } catch (error) {
      // Handle error responses
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Failed to sync Airtable';
      toast.error(errorMessage);
      setIsSyncing(false);
    }
  };

  return (
    <header
      className={`bg-white border-b border-gray-200 sticky top-0 z-40 ${isMenuOpen ? 'pt-4 pb-6 md:py-4' : 'pt-4 pb-6 md:py-4'}`}
    >
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-40">
        <div className="flex md:grid md:grid-cols-3 w-full min-h-[56px] items-center justify-between">
          <div className="flex shrink-0 items-center space-x-3">
            <Link to="/" className="flex items-center space-x-3" aria-label="Go to home">
              <img
                src={logoSrc}
                alt={`${logoSrc === logo ? 'BIOWELL' : (userOrganization || 'BIOWELL')} Logo`}
                className={
                  logoSrc === logo
                    ? 'h-10 w-auto sm:h-12'
                    : userOrganization === 'Qualevita'
                      ? 'h-14 w-auto sm:h-16'
                      : 'h-10 w-auto sm:h-12'
                }
              />
            </Link>
          </div>

          <div className="hidden md:flex md:justify-center items-center gap-6 lg:gap-8 shrink-0">
            <nav className="flex items-center space-x-8 whitespace-nowrap">
              <NavLink
                to="/profile"
                onMouseEnter={() => setProfileHovered(true)}
                onMouseLeave={() => setProfileHovered(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 font-poppins font-medium transition-colors ${isActive
                    ? 'text-[#119975]'
                    : 'text-gray-700 hover:text-[#119975]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <img
                      src={ProfileIcon}
                      alt="Profile"
                      className="h-6 w-6"
                      style={{
                        filter:
                          isActive || profileHovered
                            ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                            : 'none',
                      }}
                    />
                    <span>My Profile</span>
                  </>
                )}
              </NavLink>

              {isPatient && (
                <>
                  <NavLink
                    to="/your-advice"
                    onMouseEnter={() => setAdviceHovered(true)}
                    onMouseLeave={() => setAdviceHovered(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins font-medium transition-colors ${isActive
                        ? 'text-[#119975]'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={AdviceIcon}
                          alt="Advice"
                          className="h-6 w-6"
                          style={{
                            filter:
                              isActive || adviceHovered
                                ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                                : 'none',
                          }}
                        />
                        <span>Your Advice</span>
                      </>
                    )}
                  </NavLink>

                  <NavLink
                    to="/blood-test-summary"
                    onMouseEnter={() => setTestHovered(true)}
                    onMouseLeave={() => setTestHovered(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins font-medium transition-colors ${isActive
                        ? 'text-[#119975]'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={TestSummaryIcon}
                          alt="Test History"
                          className="h-6 w-6"
                          style={{
                            filter:
                              isActive || testHovered
                                ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                                : 'none',
                          }}
                        />
                        <span>Test History</span>
                      </>
                    )}
                  </NavLink>
                </>
              )}

              {isDoctorNurse && (
                <>
                  <NavLink
                    to="/prescription"
                    onMouseEnter={() => setPrescriptionHovered(true)}
                    onMouseLeave={() => setPrescriptionHovered(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins font-medium transition-colors ${isActive
                        ? 'text-[#119975]'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={AdviceIcon}
                          alt="Prescription"
                          className="h-6 w-6"
                          style={{
                            filter:
                              isActive || prescriptionHovered
                                ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                                : 'none',
                          }}
                        />
                        <span>Prescription</span>
                      </>
                    )}
                  </NavLink>

                  <NavLink
                    to="/order-history"
                    onMouseEnter={() => setOrderHistoryHovered(true)}
                    onMouseLeave={() => setOrderHistoryHovered(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins font-medium transition-colors ${isActive
                        ? 'text-[#119975]'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={CartIcon}
                          alt="Order History"
                          className="h-6 w-6"
                          style={{
                            filter:
                              isActive || orderHistoryHovered
                                ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                                : 'none',
                          }}
                        />
                        <span>Order History</span>
                      </>
                    )}
                  </NavLink>
                </>
              )}
            </nav>
            </div>

            <div className="hidden md:flex md:gap-2 items-center justify-center md:justify-end">
      
            <div
              ref={userMenuRef}
              className="flex items-center space-x-2 sm:space-x-3 rounded-full bg-[#F7F7F7] pl-3 sm:pl-6 pr-2 py-1 relative cursor-pointer"
              onClick={() => setIsUserMenuOpen((v) => !v)}
            >
              <span className="text-sm font-medium text-gray-900 hidden sm:block">
                {fullName}
              </span>
              <Avatar.Root className="inline-flex w-8 h-8 sm:w-12 sm:h-12 select-none items-center justify-center overflow-hidden rounded-full bg-orange-400 font-poppins">
                <Avatar.Fallback className="text-white font-poppins text-xs font-semibold leading-1 flex h-full w-full items-center justify-center bg-orange-400 rounded-full">
                  {(function () {
                    const parts = (fullName || '')
                      .trim()
                      .split(' ')
                      .filter(Boolean);
                    if (parts.length === 0) return 'IM';
                    if (parts.length === 1) return parts[0][0].toUpperCase();
                    return (
                      parts[0][0] + parts[parts.length - 1][0]
                    ).toUpperCase();
                  })()}
                </Avatar.Fallback>
              </Avatar.Root>
              {isUserMenuOpen && (
                <UserMenu
                  fullName={fullName}
                  email={
                    emailFromStore || localStorage.getItem('userEmail') || ''
                  }
                  initials={
                    (fullName || 'IM')
                      .trim()
                      .split(' ')
                      .filter(Boolean)
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || 'IM'
                  }
                  onLogout={logout}
                  isSupervisor={isSupervisor}
                  viewAsRole={viewAsRole}
                  onSwitchRole={(role) => {
                    dispatch(setViewAsRole(role));
                    if (role === 'Doctor/Nurse') navigate('/order-history');
                    else navigate('/blood-test-summary');
                    setIsUserMenuOpen(false);
                  }}
                />
              )}
            </div>
          </div>

          <button
            className={`md:hidden flex shrink-0 items-center justify-center w-11 h-11 rounded-full ${isMenuOpen ? 'bg-gray-200' : 'bg-gray-100'} text-gray-700 hover:bg-gray-200 transition-colors`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <div
            className={`md:hidden fixed inset-x-0 ${isMenuOpen ? 'top-[80px]' : 'top-[64px]'} bottom-0 overflow-y-auto bg-white border-t border-gray-200`}
          >
            <nav className="flex flex-col py-4">
              <NavLink
                to="/profile"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 font-poppins text-base transition-colors px-5 py-3 mx-5 ${isActive
                    ? 'text-[#119975] bg-[#E8F5E8] rounded-xl'
                    : 'text-gray-700 hover:text-[#119975]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <img
                      src={ProfileIcon}
                      alt="Profile"
                      className={`h-6 w-6 ${isActive ? '' : ''}`}
                      style={{
                        filter: isActive
                          ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                          : 'none',
                      }}
                    />
                    <span>My Profile</span>
                  </>
                )}
              </NavLink>

              {isPatient && (
                <>
                  <NavLink
                    to="/your-advice"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins text-base transition-colors px-5 py-3 mx-5 ${isActive
                        ? 'text-[#119975] bg-[#E8F5E8] rounded-xl'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={AdviceIcon}
                          alt="Advice"
                          className="h-6 w-6"
                          style={{
                            filter: isActive
                              ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                              : 'none',
                          }}
                        />
                        <span>Your Advice</span>
                      </>
                    )}
                  </NavLink>

                  <NavLink
                    to="/blood-test-summary"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins text-base transition-colors px-5 py-3 mx-5 ${isActive
                        ? 'text-[#119975] bg-[#E8F5E8] rounded-xl'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={TestSummaryIcon}
                          alt="Test History"
                          className="h-6 w-6"
                          style={{
                            filter: isActive
                              ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                              : 'none',
                          }}
                        />
                        <span>Test History</span>
                      </>
                    )}
                  </NavLink>
                </>
              )}

              {isDoctorNurse && (
                <>
                  <NavLink
                    to="/prescription"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins text-base transition-colors px-5 py-3 mx-5 ${isActive
                        ? 'text-[#119975] bg-[#E8F5E8] rounded-xl'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={AdviceIcon}
                          alt="Prescription"
                          className="h-6 w-6"
                          style={{
                            filter: isActive
                              ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                              : 'none',
                          }}
                        />
                        <span>Prescription</span>
                      </>
                    )}
                  </NavLink>

                  <NavLink
                    to="/order-history"
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 font-poppins text-base transition-colors px-5 py-3 mx-5 ${isActive
                        ? 'text-[#119975] bg-[#E8F5E8] rounded-xl'
                        : 'text-gray-700 hover:text-[#119975]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <img
                          src={CartIcon}
                          alt="Order History"
                          className="h-6 w-6"
                          style={{
                            filter: isActive
                              ? 'brightness(0) saturate(100%) invert(37%) sepia(36%) saturate(766%) hue-rotate(122deg) brightness(92%) contrast(91%)'
                              : 'none',
                          }}
                        />
                        <span>Order History</span>
                      </>
                    )}
                  </NavLink>
                </>
              )}

              <div className="mt-2 pt-6 pb-6 px-6 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <Avatar.Root className="inline-flex w-9 h-9 select-none items-center justify-center overflow-hidden rounded-full bg-orange-400 font-poppins">
                    <Avatar.Fallback className="text-white font-poppins text-xs font-semibold leading-1 flex h-full w-full items-center justify-center bg-orange-400 rounded-full">
                      {(function () {
                        const parts = (fullName || '')
                          .trim()
                          .split(' ')
                          .filter(Boolean);
                        if (parts.length === 0) return 'IM';
                        if (parts.length === 1) return parts[0][0].toUpperCase();
                        return (
                          parts[0][0] + parts[parts.length - 1][0]
                        ).toUpperCase();
                      })()}
                    </Avatar.Fallback>
                  </Avatar.Root>
                  <div>
                    <div className="text-base font-semibold text-gray-900">
                      {fullName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {emailFromStore || localStorage.getItem('userEmail') || ''}
                    </div>
                  </div>
                </div>
              </div>

              {isSupervisor && (
                <div className="px-6 pt-4">
                  <h3 className="text-[#2B3133] font-poppins text-sm font-semibold mb-3">
                    View as
                  </h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(setViewAsRole('Patient'));
                        navigate('/blood-test-summary');
                        setIsMenuOpen(false);
                      }}
                      className={`flex-1 font-poppins text-sm py-2.5 px-4 rounded-lg border transition-colors ${effectiveRole === 'Patient'
                        ? 'bg-[#119975] text-white border-[#119975]'
                        : 'bg-white text-[#2B3133] border-[#CBD5E0] hover:bg-[#F8F8F8]'
                        }`}
                    >
                      Patient
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(setViewAsRole('Doctor/Nurse'));
                        navigate('/order-history');
                        setIsMenuOpen(false);
                      }}
                      className={`flex-1 font-poppins text-sm py-2.5 px-4 rounded-lg border transition-colors ${effectiveRole === 'Doctor/Nurse'
                        ? 'bg-[#119975] text-white border-[#119975]'
                        : 'bg-white text-[#2B3133] border-[#CBD5E0] hover:bg-[#F8F8F8]'
                        }`}
                    >
                      Doctor/Nurse
                    </button>
                  </div>
                </div>
              )}

              {showPatientSelector && (
                <div className="px-6 pt-4">
                  <h3 className="text-[#2B3133] font-poppins text-sm font-semibold mb-3">
                    Patients ({patients.length})
                  </h3>
                  {loading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin h-5 w-5 border-2 border-[#119975] border-t-transparent rounded-full" />
                    </div>
                  ) : error ? (
                    <p className="text-red-500 font-poppins text-xs py-2">{error}</p>
                  ) : patients.length > 0 ? (
                    <div className="border border-[#CBD5E0] rounded-xl overflow-hidden bg-white">
                      <div className="p-2 border-b border-[#EDEEF0] bg-[#F8F8F8]">
                        <input
                          type="text"
                          placeholder="Search by name or customer number..."
                          value={emailSearchQuery}
                          onChange={(e) => handlePatientSearch(e.target.value)}
                          className="w-full h-[40px] px-3 rounded-lg border border-[#CBD5E0] bg-white outline-none focus:border-[#119975] focus:ring-1 focus:ring-[#119975] text-sm font-poppins"
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {filteredPatients.length > 0 ? (
                          filteredPatients.map((patient) => (
                            <div
                              key={patient.email}
                              onClick={() => handleEmailClick(patient.email)}
                              className={`text-[#2B3133] font-poppins text-xs py-2.5 px-4 hover:bg-[#F8F8F8] transition-colors border-b border-[#EDEEF0] last:border-b-0 ${linkingEmail === patient.email
                                ? 'bg-[#E7F6EF] cursor-wait opacity-75'
                                : 'cursor-pointer'
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{patientDisplayLabel(patient)}</span>
                                {linkingEmail === patient.email && (
                                  <div className="animate-spin h-3 w-3 border-2 border-[#119975] border-t-transparent rounded-full ml-2" />
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-6 text-sm text-[#889696] text-center font-poppins">
                            No patients found
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#889696] font-poppins text-xs py-2">No patients found</p>
                  )}
                </div>
              )}

              {showPatientSelector && (
                <div className="px-6 pt-4">
                  <div className="h-px bg-gray-200 mb-4" />
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    disabled={isDisconnecting}
                    className="w-full flex items-center justify-center gap-2 text-[#2B3133] font-poppins text-base hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg border border-[#CBD5E0] bg-white hover:bg-[#F8F8F8] transition-colors"
                  >
                    {isDisconnecting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-[#119975] border-t-transparent rounded-full" />
                        <span>Disconnecting...</span>
                      </>
                    ) : (
                      <span>Disconnect</span>
                    )}
                  </button>
                  {shouldPollSyncStatus && !isSyncComplete && (
                    <div className="mt-3">
                      <div className="flex items-center justify-end text-[11px] font-poppins text-[#6B7280] mb-2">
                        <span>{airtableSync.progress != null ? `${Math.round(airtableSync.progress)}%` : ''}</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
                        {airtableSync.progress != null ? (
                          <div
                            className="h-full bg-[#119975] transition-all duration-500 ease-out"
                            style={{ width: `${Math.max(0, Math.min(100, airtableSync.progress))}%` }}
                          />
                        ) : (
                          <div className="h-full w-0 bg-[#119975]" />
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleSyncAirtable(e)}
                    disabled={isSyncing || shouldPollSyncStatus || airtableSync.isRunning || linkingEmail || isDisconnecting}
                    className="mt-3 w-full flex items-center justify-center gap-2 text-[#2B3133] font-poppins text-base hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg border border-[#CBD5E0] bg-white hover:bg-[#F8F8F8] transition-colors"
                  >
                    {(isSyncing || airtableSync.isRunning) && (
                      <div className="animate-spin h-4 w-4 border-2 border-[#119975] border-t-transparent rounded-full" />
                    )}
                    <span>Sync Airtable</span>
                  </button>
                </div>
              )}

              <div className="px-6 pt-4 pb-8">
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-4 text-gray-700 border border-gray-200 rounded-lg px-6 py-3"
                  style={{ background: '#F7F7F7' }}
                >
                  <span className="w-6 h-6 grid place-items-center">
                    <img
                      src={logOutIcon}
                      alt="Log out"
                      className="w-5 h-5 object-contain"
                    />
                  </span>
                  <span className="text-base leading-none">Log Out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default PortaleHeader;
